
import type { DataKind } from "./schemaSpec.ts";
import type { EntityTypeName } from "./getRegistry.ts";

export type ContextField = {
	name: string;
	kind: DataKind;
	elementType?: EntityTypeName;
};

export type ContextScopeKind = "event" | "role";

export type ContextScopeSpec = {
	scope: string;
	kind: ContextScopeKind;
	fields: readonly ContextField[];
	label: { en: string; fr: string };
};

const EVENT_ON_DEATH: ContextScopeSpec = {
	scope: "OnDeath",
	kind: "event",
	fields: [
		{ name: "killer", kind: "player" },
		{ name: "damageContributors", kind: "list", elementType: "DamageContribution" },
		{ name: "totalDamage", kind: "number" },
	],
	label: { en: "On death", fr: "À la mort" },
};

const EVENT_ON_DAMAGE: ContextScopeSpec = {
	scope: "OnDamage",
	kind: "event",
	fields: [
		{ name: "attacker", kind: "player" },
		{ name: "amount", kind: "number" },
	],
	label: { en: "On damage", fr: "À l'attaque reçue" },
};

const EVENT_ON_TOUCH: ContextScopeSpec = {
	scope: "OnTouch",
	kind: "event",
	fields: [
		{ name: "toucher", kind: "player" },
	],
	label: { en: "On touch", fr: "Au contact" },
};

const EVENT_ON_PROXIMITY: ContextScopeSpec = {
	scope: "OnProximity",
	kind: "event",
	fields: [
		{ name: "target", kind: "string" },
		{ name: "targetPlayer", kind: "player" },
		{ name: "targetModel", kind: "any" },
		{ name: "targetItemId", kind: "string" },
		{ name: "targetInstanceId", kind: "string" },
		{ name: "targetOwner", kind: "player" },
		{ name: "proximityToken", kind: "string" },
	],
	label: { en: "On proximity", fr: "A proximite" },
};

const EVENT_ON_CONSUME: ContextScopeSpec = {
	scope: "OnConsume",
	kind: "event",
	fields: [
		{ name: "consumer", kind: "player" },
	],
	label: { en: "On consume", fr: "À la consommation" },
};

const EVENT_ON_SPAWN: ContextScopeSpec = {
	scope: "OnSpawn",
	kind: "event",
	fields: [
		{ name: "player", kind: "player" },
	],
	label: { en: "On spawn", fr: "À l'apparition" },
};

const EVENT_ON_RESPAWN: ContextScopeSpec = {
	scope: "OnRespawn",
	kind: "event",
	fields: [
		{ name: "player", kind: "player" },
	],
	label: { en: "On respawn", fr: "À la réapparition" },
};

const EVENT_ON_CREATE: ContextScopeSpec = {
	scope: "OnCreate",
	kind: "event",
	fields: [
		{ name: "player", kind: "player" },
	],
	label: { en: "On create", fr: "À la création" },
};

const ZONE_EVENT_FIELDS: readonly ContextField[] = [
	{ name: "entity", kind: "any" },
	{ name: "entityItemId", kind: "string" },
	{ name: "occupantInstanceId", kind: "string" },
	{ name: "player", kind: "player" },
	{ name: "ownerPlayer", kind: "player" },
	{ name: "placeGroupId", kind: "string" },
	{ name: "placeLabel", kind: "string" },
	{ name: "zoneItemId", kind: "string" },
];

const EVENT_ON_ENTER: ContextScopeSpec = {
	scope: "OnEnter",
	kind: "event",
	fields: ZONE_EVENT_FIELDS,
	label: { en: "On zone enter", fr: "Entree de zone" },
};

const EVENT_ON_EXIT: ContextScopeSpec = {
	scope: "OnExit",
	kind: "event",
	fields: ZONE_EVENT_FIELDS,
	label: { en: "On zone exit", fr: "Sortie de zone" },
};

const EVENT_ON_ZONE_ENTER: ContextScopeSpec = {
	scope: "OnZoneEnter",
	kind: "event",
	fields: ZONE_EVENT_FIELDS,
	label: { en: "Item enters zone", fr: "Item entre en zone" },
};

const EVENT_ON_ZONE_EXIT: ContextScopeSpec = {
	scope: "OnZoneExit",
	kind: "event",
	fields: ZONE_EVENT_FIELDS,
	label: { en: "Item exits zone", fr: "Item sort de zone" },
};

const EVENT_ON_TEAM_WIN: ContextScopeSpec = {
	scope: "OnTeamWin",
	kind: "event",
	fields: [
		{ name: "teamId", kind: "string" },
	],
	label: { en: "On team win", fr: "Victoire d'équipe" },
};

const EVENT_ON_TEAM_DEFEATED: ContextScopeSpec = {
	scope: "OnTeamDefeated",
	kind: "event",
	fields: [
		{ name: "teamId", kind: "string" },
	],
	label: { en: "On team defeated", fr: "Équipe éliminée" },
};

const EVENT_ON_MATCH_QUIT: ContextScopeSpec = {
	scope: "OnMatchQuit",
	kind: "event",
	fields: [
		{ name: "sessionId", kind: "string" },
		{ name: "matchmakerItemId", kind: "string" },
		{ name: "teamId", kind: "string" },
		{ name: "reason", kind: "string" },
		{ name: "player", kind: "player" },
	],
	label: { en: "On match quit", fr: "Sortie de match" },
};

export const NOTIFICATION_TYPES = [
	"ShopInsufficientFunds",
	"ShopMaxQuantity",
	"ShopOutOfStock",
	"QuestCompleted",
	"QuestFailed",
	"ItemAdded",
	"ItemRemoved",
	"StatLow",
	"Generic",
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

const ROLE_DIALOGUE: ContextScopeSpec = {
	scope: "Dialogue",
	kind: "role",
	fields: [
		{ name: "text", kind: "string" },
		{ name: "speaker", kind: "string" },
		{ name: "speakerName", kind: "string" },
		{ name: "itemId", kind: "string" },
	],
	label: { en: "Dialogue tree", fr: "Arbre de dialogue" },
};

const ROLE_SHOP: ContextScopeSpec = {
	scope: "Shop",
	kind: "role",
	fields: [
		{ name: "recipes", kind: "list", elementType: "Recipe" },
		{ name: "title", kind: "string" },
		{ name: "buttonLabel", kind: "string" },
		{ name: "allowFreeMode", kind: "boolean" },
		{ name: "freeModePrompt", kind: "string" },
	],
	label: { en: "Shop tree", fr: "Arbre de boutique" },
};

const ROLE_ROBUX_SHOP: ContextScopeSpec = {
	scope: "RobuxShop",
	kind: "role",
	fields: [],
	label: { en: "Robux shop tree", fr: "Boutique premium" },
};

const ROLE_FREE_MODE: ContextScopeSpec = {
	scope: "FreeMode",
	kind: "role",
	fields: [
		{ name: "slots", kind: "list", elementType: "ItemInstance" },
		{ name: "consumables", kind: "list", elementType: "ItemInstance" },
		{ name: "title", kind: "string" },
		{ name: "buttonLabel", kind: "string" },
		{ name: "freeModePrompt", kind: "string" },
	],
	label: { en: "Free mode tree", fr: "Mode libre" },
};

const ROLE_NOTIFICATION: ContextScopeSpec = {
	scope: "Notification",
	kind: "role",
	fields: [
		{ name: "text", kind: "string" },
		{ name: "type", kind: "string" },
	],
	label: { en: "Notification tree", fr: "Arbre de notification" },
};

const ROLE_CHOICE: ContextScopeSpec = {
	scope: "Choice",
	kind: "role",
	fields: [
		{ name: "prompt", kind: "string" },
		{ name: "cases", kind: "list", elementType: "ChoiceCase" },
		{ name: "speaker", kind: "string" },
		{ name: "speakerName", kind: "string" },
		{ name: "itemId", kind: "string" },
	],
	label: { en: "Choice tree", fr: "Arbre de choix" },
};

const ROLE_ACTIVE_TIMERS: ContextScopeSpec = {
	scope: "ActiveTimers",
	kind: "role",
	fields: [],
	label: { en: "Active timers widget", fr: "Widget timers actifs" },
};

const ROLE_GOAL: ContextScopeSpec = {
	scope: "Goal",
	kind: "role",
	fields: [
		{ name: "itemName", kind: "string" },
		{ name: "targetItemId", kind: "string" },
		{ name: "distance", kind: "string" },
		{ name: "isFinal", kind: "boolean" },
	],
	label: { en: "Goal compass", fr: "Boussole de quête" },
};

const ROLE_DAILY_QUEST: ContextScopeSpec = {
	scope: "DailyQuest",
	kind: "role",
	fields: [
		{ name: "dailyQuests", kind: "any" },
	],
	label: { en: "Daily quests widget", fr: "Widget quêtes journalières" },
};

const ROLE_TITLE: ContextScopeSpec = {
	scope: "Title",
	kind: "role",
	fields: [
		{ name: "text", kind: "string" },
	],
	label: { en: "Title tree", fr: "Arbre de titre" },
};

const ROLE_HUD: ContextScopeSpec = {
	scope: "HUD",
	kind: "role",
	fields: [
		{ name: "currencies", kind: "any" },
		{ name: "stats", kind: "any" },
		{ name: "actionBadges", kind: "any" },
		{ name: "goal", kind: "any" },
		{ name: "activeTimers", kind: "list", elementType: "ActiveTimer" },
	],
	label: { en: "HUD tree", fr: "Interface principale" },
};

const ROLE_INVENTORY: ContextScopeSpec = {
	scope: "Inventory",
	kind: "role",
	fields: [
		{ name: "items", kind: "list", elementType: "ItemInstance" },
		{ name: "isDelta", kind: "boolean" },
	],
	label: { en: "Inventory tree", fr: "Sac à dos" },
};

const ROLE_QUEST: ContextScopeSpec = {
	scope: "Quest",
	kind: "role",
	fields: [
		{ name: "activeQuests", kind: "list", elementType: "QuestProgress" },
	],
	label: { en: "Quest journal tree", fr: "Carnet de quêtes" },
};

const ROLE_CHECKOUT_QUEST: ContextScopeSpec = {
	scope: "CheckoutQuest",
	kind: "role",
	fields: [
		{ name: "npcTemplateId", kind: "string" },
		{ name: "title", kind: "string" },
		{ name: "prompt", kind: "string" },
		{ name: "buttonLabel", kind: "string" },
		{ name: "questItems", kind: "list", elementType: "QuestItemTarget" },
	],
	label: { en: "Checkout quest tree", fr: "Comptoir de livraison" },
};

const ROLE_TARGET_SELECTOR: ContextScopeSpec = {
	scope: "TargetSelector",
	kind: "role",
	fields: [
		{ name: "filter", kind: "string" },
		{ name: "items", kind: "list", elementType: "ItemInstance" },
		{ name: "recipes", kind: "list", elementType: "Recipe" },
		{ name: "currentInstanceId", kind: "string" },
	],
	label: { en: "Target selector tree", fr: "Sélecteur d'objet" },
};

const ROLE_DAMAGE_POPUP: ContextScopeSpec = {
	scope: "DamagePopup",
	kind: "role",
	fields: [
		{ name: "targetWorldPosition", kind: "any" },
		{ name: "damage", kind: "number" },
		{ name: "isCritical", kind: "boolean" },
	],
	label: { en: "Damage popup tree", fr: "Dégâts en l'air" },
};

const ROLE_REWARD_POPUP: ContextScopeSpec = {
	scope: "RewardPopup",
	kind: "role",
	fields: [
		{ name: "itemId", kind: "string" },
		{ name: "name", kind: "string" },
		{ name: "subtitle", kind: "string" },
		{ name: "color", kind: "string" },
	],
	label: { en: "Reward popup tree", fr: "Cadeau" },
};

const ROLE_SPLASH: ContextScopeSpec = {
	scope: "Splash",
	kind: "role",
	fields: [
		{ name: "progress", kind: "number" },
		{ name: "progressText", kind: "string" },
	],
	label: { en: "Splash tree", fr: "Écran de chargement" },
};

const ROLE_LEVEL_UP_FX: ContextScopeSpec = {
	scope: "LevelUpFX",
	kind: "role",
	fields: [
		{ name: "soundId", kind: "string" },
		{ name: "shakeIntensity", kind: "number" },
		{ name: "shakeDuration", kind: "number" },
		{ name: "sourceModel", kind: "any" },
	],
	label: { en: "Level-up FX tree", fr: "Niveau supérieur" },
};

const PREFAB_BUTTON: ContextScopeSpec = {
	scope: "Button",
	kind: "role",
	fields: [
		{ name: "label", kind: "string" },
		{ name: "icon", kind: "string" },
		{ name: "variant", kind: "string" },
		{ name: "size", kind: "string" },
		{ name: "state", kind: "string" },
		{ name: "clickStateKey", kind: "string" },
		{ name: "clickValue", kind: "any" },
		{ name: "hideInterface", kind: "string" },
		{ name: "displayInterface", kind: "string" },
	],
	label: { en: "Button prefab", fr: "Préfab Bouton" },
};

const PREFAB_BADGE: ContextScopeSpec = {
	scope: "Badge",
	kind: "role",
	fields: [
		{ name: "text", kind: "string" },
		{ name: "variant", kind: "string" },
		{ name: "size", kind: "string" },
	],
	label: { en: "Badge prefab", fr: "Préfab Badge" },
};

const PREFAB_MODAL_PANEL: ContextScopeSpec = {
	scope: "ModalPanel",
	kind: "role",
	fields: [
		{ name: "title", kind: "string" },
		{ name: "subtitle", kind: "string" },
		{ name: "closeInterfaceName", kind: "string" },
	],
	label: { en: "Modal panel prefab", fr: "Préfab Panneau modal" },
};

const PREFAB_PRODUCT_CARD: ContextScopeSpec = {
	scope: "ProductCard",
	kind: "role",
	fields: [
		{ name: "itemId", kind: "string" },
		{ name: "label", kind: "string" },
		{ name: "count", kind: "number" },
		{ name: "state", kind: "string" },
		{ name: "selectStateKey", kind: "string" },
		{ name: "selectValue", kind: "string" },
	],
	label: { en: "Product card prefab", fr: "Préfab Carte produit" },
};

const PREFAB_LIST_ROW: ContextScopeSpec = {
	scope: "ListRow",
	kind: "role",
	fields: [
		{ name: "title", kind: "string" },
		{ name: "description", kind: "string" },
		{ name: "progress", kind: "string" },
		{ name: "iconAssetId", kind: "string" },
		{ name: "state", kind: "string" },
		{ name: "actionLabel", kind: "string" },
		{ name: "actionVariant", kind: "string" },
		{ name: "actionStateKey", kind: "string" },
		{ name: "actionValue", kind: "any" },
	],
	label: { en: "List row prefab", fr: "Préfab Ligne de liste" },
};

const PREFAB_CLOSE_BUTTON: ContextScopeSpec = {
	scope: "CloseButton",
	kind: "role",
	fields: [
		{ name: "interfaceName", kind: "string" },
	],
	label: { en: "Close button prefab", fr: "Préfab Bouton fermer" },
};

const PREFAB_SLOT: ContextScopeSpec = {
	scope: "Slot",
	kind: "role",
	fields: [
		{ name: "itemId", kind: "string" },
		{ name: "count", kind: "number" },
		{ name: "removable", kind: "boolean" },
		{ name: "removeQueryKey", kind: "string" },
		{ name: "state", kind: "string" },
	],
	label: { en: "Slot prefab", fr: "Préfab Case" },
};

const ROLE_LIST_ITEM: ContextScopeSpec = {
	scope: "ListItem",
	kind: "role",
	fields: [
		{ name: "itemId", kind: "string" },
		{ name: "instanceId", kind: "string" },
		{ name: "name", kind: "string" },
		{ name: "duration", kind: "number" },
		{ name: "questId", kind: "string" },
		{ name: "removable", kind: "boolean" },
		{ name: "availableCount", kind: "number" },
	],
	label: { en: "List item (ForEach current item)", fr: "Élément de liste (item courant du ForEach)" },
};

export const CONTEXT_REGISTRY: readonly ContextScopeSpec[] = [
	EVENT_ON_DEATH,
	EVENT_ON_DAMAGE,
	EVENT_ON_TOUCH,
	EVENT_ON_PROXIMITY,
	EVENT_ON_CONSUME,
	EVENT_ON_SPAWN,
	EVENT_ON_RESPAWN,
	EVENT_ON_CREATE,
	EVENT_ON_ENTER,
	EVENT_ON_EXIT,
	EVENT_ON_ZONE_ENTER,
	EVENT_ON_ZONE_EXIT,
	EVENT_ON_TEAM_WIN,
	EVENT_ON_TEAM_DEFEATED,
	EVENT_ON_MATCH_QUIT,
	ROLE_DIALOGUE,
	ROLE_SHOP,
	ROLE_ROBUX_SHOP,
	ROLE_FREE_MODE,
	ROLE_NOTIFICATION,
	ROLE_CHOICE,
	ROLE_DAILY_QUEST,
	ROLE_ACTIVE_TIMERS,
	ROLE_GOAL,
	ROLE_TITLE,
	ROLE_HUD,
	ROLE_INVENTORY,
	ROLE_QUEST,
	ROLE_CHECKOUT_QUEST,
	ROLE_TARGET_SELECTOR,
	ROLE_DAMAGE_POPUP,
	ROLE_REWARD_POPUP,
	ROLE_SPLASH,
	ROLE_LEVEL_UP_FX,
	PREFAB_BUTTON,
	PREFAB_BADGE,
	PREFAB_MODAL_PANEL,
	PREFAB_PRODUCT_CARD,
	PREFAB_LIST_ROW,
	PREFAB_SLOT,
	PREFAB_CLOSE_BUTTON,
	ROLE_LIST_ITEM,
];

const CONTEXT_REGISTRY_BY_SCOPE: Record<string, ContextScopeSpec> =
	Object.fromEntries(CONTEXT_REGISTRY.map((s) => [s.scope, s]));

export const CONTEXT_SCOPES: readonly string[] = CONTEXT_REGISTRY.map((s) => s.scope);

export const EVENT_SCOPES: readonly string[] = CONTEXT_REGISTRY
	.filter((s) => s.kind === "event")
	.map((s) => s.scope);

export const ROLE_SCOPES: readonly string[] = CONTEXT_REGISTRY
	.filter((s) => s.kind === "role")
	.map((s) => s.scope);

export const getContextScopeSpec = (scope: string): ContextScopeSpec | undefined =>
	CONTEXT_REGISTRY_BY_SCOPE[scope];

export const isValidContextField = (scope: string, field: string): boolean => {
	const spec = CONTEXT_REGISTRY_BY_SCOPE[scope];
	if (!spec) return false;
	return spec.fields.some((f) => f.name === field);
};
